import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, type NavigateOptions, type To } from 'react-router-dom';

/**
 * Wraps useNavigate so every route change is invoked inside
 * `document.startViewTransition()`. The browser snapshots the old DOM,
 * runs the navigation, snapshots the new DOM, and crossfades between
 * them — no background-reveal flash like a plain CSS fade would have.
 *
 * `flushSync` forces React to commit the navigation synchronously inside
 * the transition callback so the new DOM is in place before the browser
 * captures its second snapshot. (React-router's own `viewTransition`
 * option only works in Data Router mode; we use BrowserRouter, so we
 * call the API directly.)
 *
 * Falls back to an instant navigation in browsers without View
 * Transitions support (Firefox, currently).
 */
export function useNavigateTransition() {
  const navigate = useNavigate();
  return useCallback(
    (to: To, options?: NavigateOptions, types?: string[]) => {
      if (typeof document.startViewTransition !== 'function') {
        navigate(to, options);
        return;
      }
      const callback = () =>
        flushSync(() => {
          navigate(to, options);
        });
      if (types?.length) {
        try {
          document.startViewTransition({ update: callback, types });
          return;
        } catch {
          // Older Chrome (pre-125) accepts only the callback form. Fall through.
        }
      }
      document.startViewTransition(callback);
    },
    [navigate],
  );
}

/**
 * Run a state update inside `document.startViewTransition()` so the browser
 * crossfades the old DOM into the new one — same mechanic as
 * `useNavigateTransition`, but for in-component view swaps (e.g. the camera
 * flow's intro ↔ camera ↔ cropping ↔ confirm modes).
 *
 * `types` is forwarded into the View Transition Types API so CSS rules can
 * scope themselves with `:active-view-transition-type(<name>)`. The photo
 * flow tags its internal swaps with `inner-swap` so the root crossfade is
 * suppressed (only the named `.photo-area` group animates, leaving the
 * header / footer / left pane / page bg completely untouched).
 *
 * Falls back to the legacy callback signature on browsers that don't yet
 * support the object form, and to an instant update in browsers without
 * view transitions at all (Firefox at time of writing).
 */
export function transitionState(update: () => void, types?: string[]) {
  if (typeof document.startViewTransition !== 'function') {
    update();
    return;
  }
  // `view-transition-name: photo-area` lives behind the `.is-inner-swapping`
  // class so it's only attached during an inner swap. Otherwise an outgoing
  // transition out of /photo (e.g. /photo → /loading) creates an un-paired
  // named group that visually swallows the right pane's contribution to the
  // root crossfade. See Photo.scss for the matching CSS.
  const isInnerSwap = !!types?.includes('inner-swap');
  const photoRight = isInnerSwap
    ? document.querySelector<HTMLElement>('.photo-right')
    : null;
  photoRight?.classList.add('is-inner-swapping');
  const cleanup = () => photoRight?.classList.remove('is-inner-swapping');
  const callback = () => flushSync(update);
  let transition: ViewTransition | undefined;
  if (types?.length) {
    try {
      transition = document.startViewTransition({ update: callback, types });
    } catch {
      // Older Chrome (pre-125) accepts only the callback form. Fall through.
    }
  }
  if (!transition) {
    transition = document.startViewTransition(callback);
  }
  transition.finished.finally(cleanup);
}
